import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import {
    FiUsers,
    FiUserCheck,
    FiUserX,
    FiTrash2,
    FiLock,
    FiUnlock,
    FiSearch,
    FiFilter,
    FiDownload,
    FiRefreshCw,
    FiChevronLeft,
    FiChevronRight,
    FiAlertCircle,
    FiPause
} from "react-icons/fi";

import {
    MdVerified,
    MdBlock,
    MdOutlineAdminPanelSettings
} from "react-icons/md";

import { HiUserGroup, HiOutlineUserRemove } from "react-icons/hi";
import { BiUserCheck, BiUserX } from "react-icons/bi";

import "../styles/AdminUserManagement.css";

export default function AdminUserManagement() {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState("USER");
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalSolicitors: 0,
        activeUsers: 0,
        suspendedUsers: 0,
        blockedUsers: 0,
        activeSolicitors: 0,
        suspendedSolicitors: 0,
        blockedSolicitors: 0
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        verificationStatus: ""
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });
    const [exportLoading, setExportLoading] = useState(false);

    /* ======================
       FETCH USERS AND STATS
    ====================== */
    useEffect(() => {
        fetchUsersAndStats();
    }, [activeTab, pagination.page, filters, searchTerm]);

    const fetchUsersAndStats = async () => {
        try {
            setLoading(true);

            const queryParams = new URLSearchParams({
                role: activeTab,
                page: pagination.page,
                limit: pagination.limit,
                ...filters,
                ...(searchTerm && { search: searchTerm })
            }).toString();

            const response = await fetch(
                `${API_BASE_URL}/api/admin/users?${queryParams}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const data = await response.json();

            if (data.success) {
                setUsers(data.data);
                if (data.stats) {
                    setStats(prev => ({
                        ...prev,
                        totalUsers: data.stats.totalUsers || 0,
                        totalSolicitors: data.stats.totalSolicitors || 0,
                        activeUsers: data.stats.activeUsers || 0,
                        suspendedUsers: data.stats.suspendedUsers || 0,
                        blockedUsers: data.stats.blockedUsers || 0,
                        activeSolicitors: data.stats.activeSolicitors || 0,
                        suspendedSolicitors: data.stats.suspendedSolicitors || 0,
                        blockedSolicitors: data.stats.blockedSolicitors || 0
                    }));
                }
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case "ACTIVE":
                return "Active";
            case "SUSPENDED":
                return "Inactive"; // ✅ UI change only
            case "BLOCKED":
                return "Blocked";
            default:
                return status;
        }
    };

    /* ======================
       EXPORT FUNCTIONALITY
    ====================== */
    const handleExport = async () => {
        try {
            const params = new URLSearchParams({
                role: activeTab,
                status: filters.status,
                verificationStatus: filters.verificationStatus,
                search: searchTerm,
                export: "excel" // 👈 KEY
            }).toString();

            const response = await fetch(
                `${API_BASE_URL}/api/admin/exportusers?${params}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `users_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            toast.success("Excel downloaded successfully");
        } catch (error) {
            toast.error("Excel export failed");
        }
    };


    /* ======================
       HEADER STATS COMPONENT
    ====================== */
    const renderHeaderStats = () => (
        <div className="user-management-stats-grid">
            <div className="stat-card stat-card-blue">
                <div className="stat-icon-wrapper">
                    <FiUsers className="stat-iconum" />
                </div>
                <div className="stat-content">
                    <h3 className="stat-value">
                        {activeTab === "USER" ? stats.totalUsers : stats.totalSolicitors}
                    </h3>
                    <p className="stat-label">
                        {activeTab === "USER" ? "Total Users" : "Total Solicitors"}
                    </p>
                    <div className="stat-breakdown">
                        <span className="stat-subtext active">
                            {activeTab === "USER" ? stats.activeUsers : stats.activeSolicitors} Active
                        </span>
                    </div>
                </div>
            </div>

            <div className="stat-card stat-card-green">
                <div className="stat-icon-wrapper">
                    <FiUserCheck className="stat-iconum" />
                </div>
                <div className="stat-content">
                    <h3 className="stat-value">
                        {activeTab === "USER" ? stats.activeUsers : stats.activeSolicitors}
                    </h3>
                    <p className="stat-label">
                        {activeTab === "USER" ? "Active Users" : "Active Solicitors"}
                    </p>
                    <span className="stat-subtext">Currently Active</span>
                </div>
            </div>

            <div className="stat-card stat-card-orange">
                <div className="stat-icon-wrapper">
                    <FiPause className="stat-iconum" />
                </div>
                <div className="stat-content">
                    <h3 className="stat-value">
                        {activeTab === "USER" ? stats.suspendedUsers : stats.suspendedSolicitors}
                    </h3>
                    <p className="stat-label">
                        {activeTab === "USER" ? "Suspended Users" : "Suspended Solicitors"}
                    </p>
                    <span className="stat-subtext">Temporarily Inactive</span>
                </div>
            </div>

            <div className="stat-card stat-card-red">
                <div className="stat-icon-wrapper">
                    <FiUserX className="stat-iconum" />
                </div>
                <div className="stat-content">
                    <h3 className="stat-value">
                        {activeTab === "USER" ? stats.blockedUsers : stats.blockedSolicitors}
                    </h3>
                    <p className="stat-label">
                        {activeTab === "USER" ? "Blocked Users" : "Blocked Solicitors"}
                    </p>
                    <span className="stat-subtext">Access Restricted</span>
                </div>
            </div>
        </div>
    );

    /* ======================
       ACTION FUNCTIONS
    ====================== */
    const handleBlockUser = async (userId, currentStatus) => {
        const isBlocked = currentStatus === "BLOCKED";
        const action = isBlocked ? "unblock" : "block";

        const result = await Swal.fire({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} User?`,
            text: isBlocked
                ? "This will restore user access."
                : "This will restrict user access to the system.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: isBlocked ? "#10b981" : "#dc2626",
            confirmButtonText: `Yes, ${action}`,
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        try {
            await fetch(`${API_BASE_URL}/api/admin/users/${userId}/status`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    status: isBlocked ? "ACTIVE" : "BLOCKED"
                })
            });

            // Update local state
            setUsers(users.map(user =>
                user._id === userId
                    ? { ...user, status: isBlocked ? "ACTIVE" : "BLOCKED" }
                    : user
            ));

            // Update stats
            updateStatsAfterStatusChange(userId, isBlocked ? "ACTIVE" : "BLOCKED", currentStatus);

            toast.success(`User ${action}ed successfully`);
        } catch (error) {
            toast.error("Failed to update user status");
        }
    };

    const handleSuspendUser = async (userId, currentStatus) => {
        const isSuspended = currentStatus === "SUSPENDED";
        const action = isSuspended ? "activate" : "suspend";

        const result = await Swal.fire({
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} User?`,
            text: isSuspended
                ? "This will restore user access."
                : "This will temporarily suspend user access.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: isSuspended ? "#10b981" : "#f59e0b",
            confirmButtonText: `Yes, ${action}`,
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        try {
            await fetch(`${API_BASE_URL}/api/admin/users/${userId}/status`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    status: isSuspended ? "ACTIVE" : "SUSPENDED"
                })
            });

            setUsers(users.map(user =>
                user._id === userId
                    ? { ...user, status: isSuspended ? "ACTIVE" : "SUSPENDED" }
                    : user
            ));

            // Update stats
            updateStatsAfterStatusChange(userId, isSuspended ? "ACTIVE" : "SUSPENDED", currentStatus);

            toast.success(`User ${action}ed successfully`);
        } catch (error) {
            toast.error("Failed to update user status");
        }
    };

    const updateStatsAfterStatusChange = (userId, newStatus, oldStatus) => {
        setStats(prev => {
            const newStats = { ...prev };
            const isUser = activeTab === "USER";

            // Decrease from old status
            if (oldStatus === "ACTIVE") {
                if (isUser) newStats.activeUsers = Math.max(0, newStats.activeUsers - 1);
                else newStats.activeSolicitors = Math.max(0, newStats.activeSolicitors - 1);
            } else if (oldStatus === "SUSPENDED") {
                if (isUser) newStats.suspendedUsers = Math.max(0, newStats.suspendedUsers - 1);
                else newStats.suspendedSolicitors = Math.max(0, newStats.suspendedSolicitors - 1);
            } else if (oldStatus === "BLOCKED") {
                if (isUser) newStats.blockedUsers = Math.max(0, newStats.blockedUsers - 1);
                else newStats.blockedSolicitors = Math.max(0, newStats.blockedSolicitors - 1);
            }

            // Increase to new status
            if (newStatus === "ACTIVE") {
                if (isUser) newStats.activeUsers = newStats.activeUsers + 1;
                else newStats.activeSolicitors = newStats.activeSolicitors + 1;
            } else if (newStatus === "SUSPENDED") {
                if (isUser) newStats.suspendedUsers = newStats.suspendedUsers + 1;
                else newStats.suspendedSolicitors = newStats.suspendedSolicitors + 1;
            } else if (newStatus === "BLOCKED") {
                if (isUser) newStats.blockedUsers = newStats.blockedUsers + 1;
                else newStats.blockedSolicitors = newStats.blockedSolicitors + 1;
            }

            return newStats;
        });
    };

    const handleDeleteUser = async (userId, userName) => {
        // CAPTCHA Verification
        const a = Math.floor(Math.random() * 9) + 1;
        const b = Math.floor(Math.random() * 9) + 1;
        const answer = a + b;

        const captchaResult = await Swal.fire({
            title: "Security Verification Required",
            html: `<p>To delete <strong>${userName}</strong>, please solve:</p>
             <div class="captcha-container">
               <div class="captcha-equation">${a} + ${b} = ?</div>
               <input type="number" class="captcha-input" placeholder="Enter answer" />
             </div>`,
            showCancelButton: true,
            confirmButtonText: "Verify",
            confirmButtonColor: "#111827",
            cancelButtonText: "Cancel",
            preConfirm: () => {
                const input = document.querySelector('.captcha-input').value;
                if (parseInt(input) !== answer) {
                    Swal.showValidationMessage("Incorrect answer. Please try again.");
                    return false;
                }
                return true;
            },
            customClass: {
                validationMessage: 'captcha-error'
            }
        });

        if (!captchaResult.isConfirmed) return;

        // Final Confirmation
        const confirmResult = await Swal.fire({
            title: "Permanent Delete?",
            html: `<p>You are about to permanently delete:</p>
             <strong>${userName}</strong>
             <p class="delete-warning">This action cannot be undone!</p>`,
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            confirmButtonText: "Yes, Delete Permanently",
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (!confirmResult.isConfirmed) return;

        try {
            await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const userToDelete = users.find(u => u._id === userId);
            setUsers(users.filter(user => user._id !== userId));

            // Update stats
            setStats(prev => {
                const newStats = { ...prev };
                if (activeTab === "USER") {
                    newStats.totalUsers = Math.max(0, newStats.totalUsers - 1);
                    if (userToDelete?.status === "ACTIVE") {
                        newStats.activeUsers = Math.max(0, newStats.activeUsers - 1);
                    } else if (userToDelete?.status === "SUSPENDED") {
                        newStats.suspendedUsers = Math.max(0, newStats.suspendedUsers - 1);
                    } else if (userToDelete?.status === "BLOCKED") {
                        newStats.blockedUsers = Math.max(0, newStats.blockedUsers - 1);
                    }
                } else {
                    newStats.totalSolicitors = Math.max(0, newStats.totalSolicitors - 1);
                    if (userToDelete?.status === "ACTIVE") {
                        newStats.activeSolicitors = Math.max(0, newStats.activeSolicitors - 1);
                    } else if (userToDelete?.status === "SUSPENDED") {
                        newStats.suspendedSolicitors = Math.max(0, newStats.suspendedSolicitors - 1);
                    } else if (userToDelete?.status === "BLOCKED") {
                        newStats.blockedSolicitors = Math.max(0, newStats.blockedSolicitors - 1);
                    }
                }
                return newStats;
            });

            toast.success("User deleted successfully");
        } catch (error) {
            toast.error("Failed to delete user");
        }
    };

    /* ======================
       RENDER VERIFICATION STATUS
    ====================== */
    const renderVerificationStatus = (status) => {
        const statusConfig = {
            PENDING: { color: "#f59e0b", icon: <FiAlertCircle />, label: "Pending" },
            VERIFIED: { color: "#10b981", icon: <MdVerified />, label: "Verified" },
            VERIFIED_LIMITED: { color: "#3b82f6", icon: <MdVerified />, label: "Limited" },
            CONDITIONAL: { color: "#8b5cf6", icon: <FiAlertCircle />, label: "Conditional" },
            REJECTED: { color: "#dc2626", icon: <BiUserX />, label: "Rejected" }
        };

        const config = statusConfig[status] || statusConfig.PENDING;

        return (
            <span className="verification-status-badge" style={{ backgroundColor: `${config.color}15`, color: config.color }}>
                {config.icon}
                <span>{config.label}</span>
            </span>
        );
    };

    /* ======================
       RENDER USER ROW
    ====================== */
    const renderUserRow = (user) => (
        <tr key={user._id} className="user-management-row">
            <td>
                <div className="user-info-cell">
                    <div className="user-avatar">
  {user.role === "USER" && user.userProfile?.photo ? (
    <img
      src={user.userProfile.photo}
      alt={user.name}
      className="user-avatar-img"
    />
  ) : user.role === "SOLICITOR" && user.profile?.photo ? (
    <img
      src={user.profile.photo}
      alt={user.name}
      className="user-avatar-img"
    />
  ) : (
    <div className="avatar-fallback">
      {user.name?.charAt(0)?.toUpperCase()}
    </div>
  )}
</div>

                    <div className="user-details">
                        <div className="user-name">{user.name}</div>
                        <div className="user-meta">
                            <span className="user-email">{user.email}</span>
                        </div>
                    </div>
                </div>
            </td>

            {/* Status Column moved to left side - 2nd column */}
            <td>
                <div className="status-cell">
                    <span className={`status-badge status-${user.status.toLowerCase()}`}>
                        {user.status === "ACTIVE" && <BiUserCheck />}
                        {user.status === "SUSPENDED" && <FiPause />}
                        {user.status === "BLOCKED" && <MdBlock />}
                        <span>{getStatusLabel(user.status)}</span>
                    </span>
                </div>
            </td>

            {activeTab === "SOLICITOR" && (
                <>
                    <td>
                        <div className="solicitor-id">
                            {user.solicitorId || <span className="no-id">Not Assigned</span>}
                        </div>
                    </td>
                    <td>
                        {renderVerificationStatus(user.verificationStatus)}
                    </td>
                </>
            )}

            <td>
                <div className="action-buttons">
                    {user.status === "BLOCKED" ? (
                        <button
                            className="action-btn unblock-btn"
                            onClick={() => handleBlockUser(user._id, user.status)}
                            title="Unblock User"
                        >
                            <FiUnlock size={16} />
                            <span>Unblock</span>
                        </button>
                    ) : (
                        <>
                            <button
                                className="action-btn block-btn"
                                onClick={() => handleBlockUser(user._id, user.status)}
                                title="Block User"
                            >
                                <FiLock size={16} />
                                <span>Block</span>
                            </button>

                            <button
                                className="action-btn suspend-btn"
                                onClick={() => handleSuspendUser(user._id, user.status)}
                                title={user.status === "SUSPENDED" ? "Activate User" : "Suspend User"}
                            >
                                {user.status === "SUSPENDED" ? <BiUserCheck size={16} /> : <FiPause size={16} />}
                                <span>{user.status === "SUSPENDED" ? "Activate" : "Suspend"}</span>
                            </button>
                        </>
                    )}

                    <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteUser(user._id, user.name)}
                        title="Delete User"
                    >
                        <FiTrash2 size={16} />
                        <span>Delete</span>
                    </button>
                </div>
            </td>
        </tr>
    );

    /* ======================
       MAIN RENDER
    ====================== */
    return (
        <div className="luxury-user-management">
            <div className="user-management-header">
                <div className="header-left">
                    <h1 className="page-title">User Management</h1>
                    <p className="page-subtitle">
                        Manage system users and solicitor accounts
                    </p>
                </div>
                <div className="header-right">
                    <button className="refresh-btn" onClick={fetchUsersAndStats}>
                        <FiRefreshCw size={18} />
                        <span>Refresh</span>
                    </button>
                    <button
                        className="export-btn"
                        onClick={handleExport}
                        disabled={exportLoading || users.length === 0}
                        title={users.length === 0 ? "No data to export" : "Export to Excel"}
                    >
                        {exportLoading ? (
                            <>
                                <div className="export-spinner"></div>
                                <span>Exporting...</span>
                            </>
                        ) : (
                            <>
                                <FiDownload size={18} />
                                <span>Export ({users.length})</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Header Stats */}
            {renderHeaderStats()}

            {/* Main Content */}
            <div className="user-management-content">
                {/* Tabs and Controls */}
                <div className="content-controls">
                    <div className="tabs-container">
                        <button
                            className={`tab-btn ${activeTab === "USER" ? "active" : ""}`}
                            onClick={() => setActiveTab("USER")}
                        >
                            <HiUserGroup size={18} />
                            <span>Users ({stats.totalUsers})</span>
                        </button>
                        <button
                            className={`tab-btn ${activeTab === "SOLICITOR" ? "active" : ""}`}
                            onClick={() => setActiveTab("SOLICITOR")}
                        >
                            <MdOutlineAdminPanelSettings size={18} />
                            <span>Solicitors ({stats.totalSolicitors})</span>
                        </button>
                    </div>

                    <div className="controls-right">
                        <div className="search-box">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="filter-dropdown">
                            <FiFilter className="filter-icon" />
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="filter-select"
                            >
                                <option value="">All Status</option>
                                <option value="ACTIVE">Active</option>
                                <option value="SUSPENDED">Suspended</option>
                                <option value="BLOCKED">Blocked</option>
                            </select>
                        </div>

                        {activeTab === "SOLICITOR" && (
                            <div className="filter-dropdown">
                                <select
                                    value={filters.verificationStatus}
                                    onChange={(e) => setFilters({ ...filters, verificationStatus: e.target.value })}
                                    className="filter-select"
                                >
                                    <option value="">All Verification</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="VERIFIED">Verified</option>
                                    <option value="VERIFIED_LIMITED">Limited</option>
                                    <option value="CONDITIONAL">Conditional</option>
                                    <option value="REJECTED">Rejected</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="table-container">
                    {loading ? (
                        <div className="adminloading-state">
                            <div className="adminloading-spinner"></div>
                            <p>Loading users...</p>
                        </div>
                    ) : (
                        <table className="user-management-table">
                            <thead>
                                <tr>
                                    <th>User Information</th>
                                    <th>Status</th>
                                    {activeTab === "SOLICITOR" && <th>Solicitor ID</th>}
                                    {activeTab === "SOLICITOR" && <th>Verification</th>}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length > 0 ? (
                                    users.map(renderUserRow)
                                ) : (
                                    <tr className="no-data-row">
                                        <td colSpan={activeTab === "SOLICITOR" ? 5 : 4}>
                                            <div className="no-data">
                                                <FiUserX size={48} />
                                                <p>No users found</p>
                                                <small>Try adjusting your search or filters</small>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                className="pagination-btn"
                                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                disabled={pagination.page === 1}
                            >
                                <FiChevronLeft size={18} />
                                <span>Previous</span>
                            </button>

                            <div className="page-numbers">
                                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                    let pageNum;
                                    if (pagination.pages <= 5) {
                                        pageNum = i + 1;
                                    } else if (pagination.page <= 3) {
                                        pageNum = i + 1;
                                    } else if (pagination.page >= pagination.pages - 2) {
                                        pageNum = pagination.pages - 4 + i;
                                    } else {
                                        pageNum = pagination.page - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            className={`page-btn ${pagination.page === pageNum ? "active" : ""}`}
                                            onClick={() => setPagination({ ...pagination, page: pageNum })}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                className="pagination-btn"
                                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                disabled={pagination.page === pagination.pages}
                            >
                                <span>Next</span>
                                <FiChevronRight size={18} />
                            </button>

                            <div className="page-info">
                                Showing {(pagination.page - 1) * pagination.limit + 1}-
                                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}